import React, { useRef, useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import { fetchAllPvSize } from '../../services/allPvSizeService';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { parseSourceNumber } from '../../utils/sourceNumeric';
import { isSolarApprovalCohortYear } from '../../services/solarAnalysisCutoff';

const formatSupabaseError = (err: unknown) => {
  if (!err || typeof err !== 'object') return 'Unknown error';
  const anyErr = err as { message?: string; details?: string; hint?: string; code?: string };
  const parts = [anyErr.message, anyErr.details, anyErr.hint, anyErr.code].filter(Boolean);
  return parts.length ? parts.join(' | ') : 'Unknown error';
};

interface SolarPerCapitaComparisonChartProps {
  location: string;
  geofips: number;
}

export const SolarPerCapitaComparisonChart: React.FC<SolarPerCapitaComparisonChartProps> = ({ location, geofips }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPerCapitaData = async () => {
      if (!location || !geofips) return;

      try {
        setLoading(true);
        setError(null);

        const [locationSolarData, locationPopulationData] = await Promise.all([
          fetchAllPvSize({ select: 'year, sector, total_watts', geofips }),
          supabaseClient
            .from('population')
            .select('year, population')
            .eq('geofips', geofips)
            .order('year', { ascending: true }),
        ]);

        if (locationSolarData.error) throw locationSolarData.error;
        if (locationPopulationData.error) throw locationPopulationData.error;

        const sectorTotalsKw = new Map<string, number>();
        locationSolarData.data?.forEach((record) => {
          const year = parseInt(record.year.toString(), 10);
          if (!isSolarApprovalCohortYear(year)) return;
          const sector = record.sector || 'unknown';
          const watts = parseSourceNumber(record.total_watts);
          if (watts == null) return;
          sectorTotalsKw.set(sector, (sectorTotalsKw.get(sector) ?? 0) + watts / 1000);
        });

        let populationYear = 0;
        let population = 0;
        locationPopulationData.data?.forEach((record) => {
          const year = parseInt(record.year.toString(), 10);
          if (!isSolarApprovalCohortYear(year)) return;
          const pop = parseSourceNumber(record.population);
          if (pop == null || pop <= 0) return;
          if (year >= populationYear) {
            populationYear = year;
            population = pop;
          }
        });

        if (population <= 0 || sectorTotalsKw.size === 0) {
          setError('Solar Project Sites data are unavailable for this per-capita snapshot.');
          return;
        }

        const sectorLabels = Array.from(sectorTotalsKw.keys()).map(
          (s) => s.charAt(0).toUpperCase() + s.slice(1)
        );
        const perCapitaValues = Array.from(sectorTotalsKw.values()).map((kw) => kw / population);

        if (chartRef.current && sectorLabels.length > 0) {
          const ctx = chartRef.current.getContext('2d');
          if (ctx) {
            if (chartInstance.current) chartInstance.current.destroy();

            chartInstance.current = new Chart(ctx, {
              type: 'bar',
              data: {
                labels: sectorLabels,
                datasets: [
                  {
                    label: `kW DC per resident (${populationYear} population)`,
                    data: perCapitaValues,
                    backgroundColor: '#3b82f6',
                    borderColor: '#1e3a8a',
                    borderWidth: 1,
                  },
                ],
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: standardTitle(
                    `Current Solar Capacity Intensity by Sector — ${location} (${populationYear})`
                  ),
                  legend: standardLegend,
                  tooltip: {
                    callbacks: {
                      label: (context: any) => {
                        const value = context.parsed.y;
                        if (value == null || !Number.isFinite(value)) return undefined;
                        return `${value.toFixed(4)} kW DC per resident`;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Sector (represented Project Sites DC through analysis cutoff)',
                      font: { size: 14, weight: 'bold' },
                    },
                  },
                  y: {
                    title: {
                      display: true,
                      text: 'Capacity per resident (kW DC)',
                      font: { size: 14, weight: 'bold' },
                    },
                    beginAtZero: true,
                  },
                },
              },
            });
          }
        }
      } catch (err) {
        console.error('Error fetching per capita solar data:', err);
        const message = err instanceof Error ? err.message : formatSupabaseError(err);
        setError(`Failed to load per capita solar data: ${message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchPerCapitaData();

    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
    };
  }, [location, geofips]);

  if (loading) {
    return <div>Loading solar per capita snapshot...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div data-solar-view="perCapita" data-solar-loaded="true" style={{ height: '380px', width: '100%' }}>
      <canvas ref={chartRef} />
    </div>
  );
};
